import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './LegalConsentGate.css';
import PageButton from '../../elements/PageButton/PageButton';
import LegalDocumentPage from '../LegalDocumentPage/LegalDocumentPage';
import {LICENSE_AGREEMENT_CONTENT} from '../../../configs/legal/licenseAgreement';
import {PRIVACY_POLICY_CONTENT} from '../../../configs/legal/privacyPolicy';
import {setPlayerAuth} from '../../../redux/actions';
import {API_BASE} from '../../../utils/apiConfig';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';

const SHAKE_DURATION_MS = 500;

const VIEWS = {
  GATE: 'gate',
  LICENSE: 'license',
  PRIVACY: 'privacy',
};

function LegalConsentGate() {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.private.auth);

  const [view, setView] = useState(VIEWS.GATE);
  // Два документа принимаются одной галочкой (ссылки на них раздельные), а
  // подтверждение возраста — отдельной: игра только для тех, кому исполнилось 18
  const [isDocumentsChecked, setIsDocumentsChecked] = useState(false);
  const [isAdultChecked, setIsAdultChecked] = useState(false);
  const [shakeDocuments, setShakeDocuments] = useState(false);
  const [shakeAdult, setShakeAdult] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  usePageAnalytics('LegalConsentGate');

  if (view === VIEWS.LICENSE) {
    return (
      <LegalDocumentPage
        pageId="LegalConsentGate"
        content={LICENSE_AGREEMENT_CONTENT}
        onBack={() => setView(VIEWS.GATE)}
      />
    );
  }

  if (view === VIEWS.PRIVACY) {
    return (
      <LegalDocumentPage
        pageId="LegalConsentGate"
        content={PRIVACY_POLICY_CONTENT}
        onBack={() => setView(VIEWS.GATE)}
      />
    );
  }

  const handleAccept = async () => {
    if (!isDocumentsChecked || !isAdultChecked) {
      if (!isDocumentsChecked) {
        setShakeDocuments(true);
        setTimeout(() => setShakeDocuments(false), SHAKE_DURATION_MS);
      }
      if (!isAdultChecked) {
        setShakeAdult(true);
        setTimeout(() => setShakeAdult(false), SHAKE_DURATION_MS);
      }
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/accept-legal-documents`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({privacyPolicy: true, licenseAgreement: true, adultConfirmed: true}),
      });
      if (!res.ok) {
        throw new Error('Failed to accept legal documents');
      }
      const data = await res.json();
      if (data.user) {
        dispatch(setPlayerAuth({...auth, ...data.user}));
      }
    } catch (e) {
      console.error('[LegalConsentGate] Accept error', e);
      setError('Не удалось сохранить согласие. Проверьте соединение и попробуйте еще раз.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="legal-consent-gate">
      <h1 className="legal-page-title">Прежде чем продолжить</h1>
      <p className="legal-consent-intro">
        Чтобы использовать сервисы "Стратегикон", необходимо ознакомиться и принять условия
        следующих документов, а также подтвердить, что вам исполнилось 18 лет:
      </p>

      <div className="legal-consent-checkboxes">
        {/* Одна галочка на оба документа, ссылки на них раздельные. Текст между
            ссылками — тоже подпись галочки: клик по нему ставит отметку */}
        <div className={`legal-consent-checkbox-row ${shakeDocuments ? 'legal-consent-checkbox-row_shake' : ''}`}>
          <input
            type="checkbox"
            id="legal-consent-documents"
            checked={isDocumentsChecked}
            onChange={(e) => setIsDocumentsChecked(e.target.checked)}
          />
          <div className="legal-consent-checkbox-text">
            <label htmlFor="legal-consent-documents">
              Я ознакомился(-лась) и принимаю условия
            </label>{' '}
            <button
              type="button"
              className="legal-consent-doc-link"
              onClick={() => setView(VIEWS.LICENSE)}
            >
              Лицензионного соглашения
            </button>{' '}
            <label htmlFor="legal-consent-documents">и</label>{' '}
            <button
              type="button"
              className="legal-consent-doc-link"
              onClick={() => setView(VIEWS.PRIVACY)}
            >
              Политики конфиденциальности
            </button>
          </div>
        </div>

        <div className={`legal-consent-checkbox-row ${shakeAdult ? 'legal-consent-checkbox-row_shake' : ''}`}>
          <input
            type="checkbox"
            id="legal-consent-adult"
            checked={isAdultChecked}
            onChange={(e) => setIsAdultChecked(e.target.checked)}
          />
          <div className="legal-consent-checkbox-text">
            <label htmlFor="legal-consent-adult">
              Подтверждаю, что мне исполнилось 18 лет
            </label>
          </div>
        </div>
      </div>

      <p className="legal-consent-cookie-notice">
        Сайт использует файлы cookie, необходимые для авторизации и работы сервиса, а
        также для улучшения пользовательского опыта. Кроме того, на сайте работают
        счётчики Яндекс Метрики (включая запись сеансов «Вебвизор») и VK Рекламы —
        они собирают сведения о посещениях и действиях на сайте для оценки
        эффективности рекламы. Продолжая пользоваться сайтом, вы соглашаетесь с их
        использованием — подробнее в Политике конфиденциальности.
      </p>

      {error && <p className="legal-consent-error">{error}</p>}

      <div className="main-menu-buttons">
        <PageButton
          className="legal-consent-accept-button"
          onClick={handleAccept}
          pageId="LegalConsentGate"
          buttonId="accept"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Сохраняем...' : 'Принять'}
        </PageButton>
      </div>
    </div>
  );
}

export default LegalConsentGate;
